"use client"
import { useEffect, useRef, Suspense } from "react"
import React from "react"

import Image from "next/image"
import Link from "next/link"
// First, add the useInView hook import at the top with the other imports
import { motion, useInView } from "framer-motion"
import { Instagram, Globe } from "lucide-react"
// Lazy loading of COLLECTIONS for optimization
const COLLECTIONS = () => import("@/lib/api").then(module => module.COLLECTIONS)
import { ShaderAvatarBackground } from "@/components/shader-avatar-background"
import { useMobile } from "@/hooks/use-mobile"
import { ContentSection } from "@/components/content-section"
// First, let's add the necessary imports for the ThreeColumnSection component
import { ThreeColumnSection } from "@/components/three-column-section"
// Update the import for MiniPoll at the top of the file
// import { MiniPoll } from "@/components/mini-poll"
// Add the import for SupportedChains at the top of the file:
import { SupportedChains } from "@/components/supported-chains"
// Import the XIcon component at the top of the file:
import { XIcon } from "@/components/x-icon"
// Import ShaderButton for the main CTA button:
import { ShaderButton } from "@/components/shader-button"
// Import Accordion components for FAQ section:
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion"
import { Plus, Minus } from "lucide-react"

// Animation variants for staggered reveal
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1, // Reduced delay between elements
      delayChildren: 0.1,   // Reduced initial delay
    },
  },
}

const itemVariants = {
  hidden: { opacity: 0, y: -20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.5,
      ease: "easeOut",
    },
  },
}

// Now add the ScrollAnimatedSection component at the top of the file, after the imports
// Add this function before the About component:

function ScrollAnimatedSection({ children }: { children: React.ReactNode }) {
  const ref = React.useRef(null)
  const isInView = useInView(ref, { once: true, amount: 0.1 }) // Optimized: once: true, amount: 0.1

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 20 }}
      animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
      transition={{ duration: 0.4, ease: "easeOut" }} // Reduced duration from 0.6 to 0.4
    >
      {children}
    </motion.div>
  )
}

// Update the About page component to include the combined sections with white background
export default function AboutClient() {
  const isMobile = useMobile()

  // Then update the columns definition in the About component
  const columns = [
    {
      title: "How the Totem Is Built",
      content: (
        <>
          <p className="text-sm text-gray-600">
            Each totem is unique. It's constructed from blocks, with each block representing proof of ownership —
            directly tied to a token in your wallet.
          </p>
          <p className="text-sm text-gray-600">
            Only collections that have stood the test of time and earned the community's trust are included in the
            catalog.
          </p>
        </>
      ),
    },
    {
      title: "What's Next",
      content: (
        <>
          <p className="text-sm text-gray-600">We don't have a strict roadmap — we're building this for fun.</p>
          <p className="text-sm text-gray-600">Right now, our main focus is on designing and adding more blocks.</p>
          <p className="text-sm text-gray-600">
            Have an idea or suggestion? Feel free to reach out to{" "}
            <Link
              href="https://twitter.com/ilyakazakov"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-purple hover:underline"
            >
              Ilya
            </Link>{" "}
            or{" "}
            <Link
              href="https://twitter.com/antongridz"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-purple hover:underline"
            >
              Anton
            </Link>{" "}
            on X.
          </p>
        </>
      ),
    },
    {
      title: "Minting",
      content: (
        <>
          <p className="text-sm text-gray-600 mb-4">
            Anyone holding at least one token from the selected collections can mint a Totem for 0.003 ETH (+ gas) — or free if it includes @ilyakazakov's blocks.
          </p>
          <p className="text-sm text-gray-600">
            After minting, your Totem can be edited and expanded as you collect more NFTs from featured collections.
          </p>
        </>
      ),
    },
  ]

  return (
    <main className="flex flex-1 flex-col items-center p-4 md:p-8 lg:pt-12 pt-32 md:pt-36 lg:pt-40">
      <div className="w-full max-w-6xl mx-auto mb-20">
        <motion.div
          className="flex flex-col items-center"
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
          {/* Updated title section with subtitle */}
          <motion.div className="text-center mb-4 md:mb-6" variants={itemVariants}>
            <motion.div
              className="w-full max-w-4xl mx-auto mb-8 md:mb-12"
              variants={itemVariants}
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <div className="relative overflow-hidden">
                <Image
                  // Proxy through our cacheable endpoint
                  src={`/api/remote-image?url=${encodeURIComponent("https://hebbkx1anhila5yf.public.blob.vercel-storage.com/hero-chains%402x-G7ZqHD4dFPeqZIN5jsAU7txmhxivHv.png")}`}
                  alt="NFT Totem Chains"
                  width={1200}
                  height={300}
                  className="w-full h-auto transform-gpu pb-6 md:pb-8"
                  priority
                  loading="eager"
                />
              </div>
            </motion.div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 mb-3 md:mb-4 tracking-tight font-funnel">
              Non-Fungible Totem
            </h1>
          </motion.div>

          <motion.div variants={itemVariants}>
            <ContentSection centered maxWidth="3xl" spacing="relaxed">
              <p className="text-md md:text-lg leading-[1.5] text-gray-600">
                NFTOTEM began in 2024 as Ilya's side experiment: a living totem that mirrors the NFTs in your wallet. A
                few prototypes later, we realised its broader appeal — now any collector can build a personal totem
                shaped entirely by the tokens they own.
              </p>
            </ContentSection>
          </motion.div>

          {/* White background container for all sections */}
          <motion.div
            variants={itemVariants}
            className="w-full mt-12 bg-white rounded-3xl shadow-sm overflow-hidden border border-solid border-gray-200"
          >
            {/* Three-column section with scroll animation */}
            <ScrollAnimatedSection>
              <div className="p-8 md:p-12">
                <ThreeColumnSection
                  columns={columns}
                  titleClassName="text-lg md:text-xl" // Adjust size here
                />
              </div>
            </ScrollAnimatedSection>

            {/* Divider */}
            <div className="border-t border-gray-200 w-full"></div>

            {/* Team section with scroll animation */}
            <ScrollAnimatedSection>
              <div className="p-8 md:p-12">
                <h2 className="text-3xl font-bold text-gray-900 mb-12 tracking-tight font-funnel text-center">
                  Our Team
                </h2>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  {/* Ilya Kazakov */}
                  <div className="flex flex-row items-start gap-6">
                    <div className="w-[101px] h-[120px] overflow-hidden relative flex-shrink-0">
                      {/* Hexagon clip path */}
                      <div
                        className="absolute inset-0"
                        style={{ clipPath: "polygon(50% 0%, 100% 25%, 100% 75%, 50% 100%, 0% 75%, 0% 25%)" }}
                      >
                        <ShaderAvatarBackground />
                      </div>
                      <div
                        className="absolute inset-0 flex items-center justify-center"
                        style={{ clipPath: "polygon(50% 0%, 100% 25%, 100% 75%, 50% 100%, 0% 75%, 0% 25%)" }}
                      >
                        <Image
                          src={`/api/remote-image?url=${encodeURIComponent("https://hebbkx1anhila5yf.public.blob.vercel-storage.com/ilya_kazakov_avatar-COqKSNVtEYGDjAeQBscWwQPh0MrZgK.webp")}`}
                          alt="Ilya Kazakov"
                          width={120}
                          height={120}
                          className="object-contain z-10"
                        />
                      </div>
                    </div>
                    <div className="flex flex-col">
                      <h3 className="text-lg md:text-xl  font-semibold mb-1 font-funnel">Ilya Kazakov</h3>
                      {/* <p className="text-brand-purple mb-3">Art & Design</p> */}
                      <p className="text-sm text-gray-600 mb-4">
                        Digital artist specializing in NFT art and creative illustrations.
                      </p>
                      <div className="flex space-x-4">
                        <Link
                          href="https://twitter.com/ilyakazakov"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-gray-500 hover:text-brand-purple transition-colors"
                        >
                          <XIcon size={20} />
                          <span className="sr-only">Twitter</span>
                        </Link>
                        <Link
                          href="https://instagram.com/risuyu"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-gray-500 hover:text-brand-purple transition-colors"
                        >
                          <Instagram size={20} />
                          <span className="sr-only">Instagram</span>
                        </Link>
                        <Link
                          href="https://ilyakazakov.com"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-gray-500 hover:text-brand-purple transition-colors"
                        >
                          <Globe size={20} />
                          <span className="sr-only">Website</span>
                        </Link>
                      </div>
                    </div>
                  </div>

                  {/* Anton Gridz */}
                  <div className="flex flex-row items-start gap-6">
                    <div className="w-[101px] h-[120px] overflow-hidden relative flex-shrink-0">
                      {/* Hexagon clip path */}
                      <div
                        className="absolute inset-0"
                        style={{ clipPath: "polygon(50% 0%, 100% 25%, 100% 75%, 50% 100%, 0% 75%, 0% 25%)" }}
                      >
                        <ShaderAvatarBackground />
                      </div>
                      <div
                        className="absolute inset-0 flex items-center justify-center"
                        style={{ clipPath: "polygon(50% 0%, 100% 25%, 100% 75%, 50% 100%, 0% 75%, 0% 25%)" }}
                      >
                        <Image
                          src={`/api/remote-image?url=${encodeURIComponent("https://hebbkx1anhila5yf.public.blob.vercel-storage.com/anton_gridz_avatar-nBDOphWaVZpQF1VMTuFHdmED1ZjMmg.webp")}`}
                          alt="Anton Gridz"
                          width={120}
                          height={120}
                          className="object-contain z-10"
                        />
                      </div>
                    </div>
                    <div className="flex flex-col">
                      <h3 className="text-lg md:text-xl font-semibold mb-1 font-funnel">Anton Gridz</h3>
                      {/* <p className="text-brand-purple mb-3">UI Design & Development</p> */}
                      <p className="text-sm text-gray-600 mb-4">
                        Designer and developer creating intuitive and visually appealing web applications.
                      </p>
                      <div className="flex space-x-4">
                        <Link
                          href="https://twitter.com/antongridz"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-gray-500 hover:text-brand-purple transition-colors"
                        >
                          <XIcon size={20} />
                          <span className="sr-only">Twitter</span>
                        </Link>
                        <Link
                          href="https://instagram.com/antongridz"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-gray-500 hover:text-brand-purple transition-colors"
                        >
                          <Instagram size={20} />
                          <span className="sr-only">Instagram</span>
                        </Link>
                        <Link
                          href="https://antongridz.com"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-gray-500 hover:text-brand-purple transition-colors"
                        >
                          <Globe size={20} />
                          <span className="sr-only">Website</span>
                        </Link>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </ScrollAnimatedSection>

            {/* Divider */}
            <div className="border-t border-gray-200 w-full"></div>

          {/* Quote section */}
          <ScrollAnimatedSection>
            <div className="w-full bg-violet-50 py-12 md:py-16">
              <div className="max-w-5xl mx-auto px-8 md:px-12">
                <div className="flex flex-col md:flex-row items-start gap-4 md:gap-6">
                  {/* Quote icon */}
                  <div className="flex-shrink-0 -mt-2">
                    <svg width="48" height="48" viewBox="0 0 25 25" fill="none" xmlns="http://www.w3.org/2000/svg" className="text-violet-600">
                      <path d="M19.2324 20.1016C21.1654 20.1016 22.7324 18.5339 22.7324 16.6V14.5991C22.7324 12.6653 21.1654 11.0976 19.2324 11.0976H18.7324V4.59466C18.7324 4.42854 18.65 4.27325 18.5124 4.18022C18.1521 3.93656 17.634 4.32119 17.3493 4.49282C16.9471 4.73532 16.4128 5.11128 15.8781 5.64861C14.8024 6.72952 13.7324 8.45893 13.7324 11.0261V16.601C13.7324 18.535 15.2996 20.1016 17.2324 20.1016H19.2324Z" fill="currentColor"/>
                      <path d="M8.23242 20.1016C10.1654 20.1016 11.7324 18.5339 11.7324 16.6V14.5991C11.7324 12.6653 10.1654 11.0976 8.23242 11.0976H7.73242V4.59466C7.73242 4.42854 7.64999 4.27325 7.51242 4.18022C7.15208 3.93656 6.63398 4.32119 6.34933 4.49282C5.94714 4.73532 5.41278 5.11128 4.87808 5.64861C3.80246 6.72952 2.73242 8.45893 2.73242 11.0261V16.601C2.73242 18.535 4.29963 20.1016 6.23242 20.1016H8.23242Z" fill="currentColor"/>
                    </svg>
                  </div>
                  
                  {/* Quote content */}
                  <div className="flex-1">
                    <p className="text-xl md:text-2xl lg:text-3xl text-gray-900 leading-relaxed font-funnel mb-6">
                      The NFTOTEM mint was one of the most uniquely brilliant activations I've seen in a long time. The Gods of Olympus would be scared of the size it produced.
                    </p>
                    
                    {/* Author */}
                    <div className="flex items-center gap-3 mt-6">
                      <div className="w-12 h-12 rounded-full bg-gray-200 overflow-hidden flex-shrink-0">
                        <Image
                          src="/images/drdavecoin-avatar.png"
                          alt="DRDAVECOIN.ETH"
                          width={64}
                          height={64}
                          className="object-cover"
                        />
                      </div>
                      <div>
                        <p className="font-funnel font-semibold text-sm text-gray-900">DRDAVECOIN.ETH</p>
                        <p className="font-funnel text-sm text-gray-600">COLLECTOR</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </ScrollAnimatedSection>

            {/* Divider */}
            <div className="border-t border-gray-200 w-full"></div>

            {/* Supported Collections section with scroll animation */}
            {/* Supported Collections section with scroll animation - COMMENTED OUT */}
            {/*
            <ScrollAnimatedSection>
              <div className="p-8 md:p-12">
                <h2 className="text-3xl font-bold text-gray-900 mb-8 tracking-tight font-funnel text-center">
                  Supported Collections
                </h2>

                <Suspense fallback={
                  <div className="flex justify-center items-center h-32">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-purple"></div>
                  </div>
                }>
                  <CollectionTicker />
                </Suspense>
              </div>
            </ScrollAnimatedSection>
            <div className="border-t border-gray-200 w-full"></div>
            */}

            {/* Supported Chains section with scroll animation */}
            <ScrollAnimatedSection>
              <div className="p-8 md:p-12">
                <SupportedChains />
              </div>
            </ScrollAnimatedSection>

            {/* Divider */}
            <div className="border-t border-gray-200 w-full"></div>

            {/* FAQ section with scroll animation */}
            <ScrollAnimatedSection>
              <div className="p-8 md:p-12">
                <h2 className="text-3xl font-bold text-gray-900 mb-8 tracking-tight font-funnel text-center">
                  FAQ
                </h2>

                <Accordion type="single" collapsible className="w-full max-w-4xl mx-auto">
                  {[
                    {
                      question: "How can I check my Totem?",
                      answer: (
                        <p className="text-gray-700 text-sm">
                          It's easy! Just enter your wallet address or ENS name (e.g. <em>username.eth</em>) and click{" "}
                          <strong>"Reveal my Totem."</strong> If your NFT portfolio contains at least one token from the
                          supported collections, your Totem will appear.
                        </p>
                      ),
                    },
                    {
                      question: "I don't see my Totem after minting",
                      answer: (
                        <p className="text-gray-700 text-sm">
                          Go to your Totem page on OpenSea and click <strong>"Refresh Metadata."</strong> Sometimes
                          OpenSea caches metadata and updates may take time. Please be patient — if needed, we can
                          always help you fix it.
                        </p>
                      ),
                    },
                    {
                      question: "How can I edit my Totem?",
                      answer: (
                        <p className="text-gray-700 text-sm">
                          You can edit your Totem anytime after minting. Simply log in with your wallet, open{" "}
                          <strong>Wallet Address → My Totem</strong> in the menu, and click <strong>Edit Totem.</strong>{" "}
                          If you collected new NFTs from supported collections, they will appear only after editing.
                          Editing is free — you only pay the gas fee.
                        </p>
                      ),
                    },
                    {
                      question: "How do I add new blocks?",
                      answer: (
                        <p className="text-gray-700 text-sm">
                          Each block represents a supported NFT collection. To add one, acquire at least one NFT from
                          that collection. Then log in on our site, go to <strong>Wallet Address → My Totem</strong>,
                          and click <strong>Edit Totem</strong> to refresh your metadata and see the new block.
                        </p>
                      ),
                    },
                    {
                      question: "How does the Ranking system work?",
                      answer: (
                        <p className="text-gray-700 text-sm">
                          Ranking is automatically calculated based on the rarity and number of traits in your Totem. It
                          uses the <strong>OpenRarity</strong> standard and updates after you edit your Totem. Sometimes
                          recalculations may take a while — don't worry, it will update!
                        </p>
                      ),
                    },
                  ].map((item, index) => (
                    <AccordionItem key={index} value={`item-${index}`} className="border-b border-gray-200 pb-7">
                      <AccordionTrigger className="text-left pt-6 pb-0 hover:no-underline group [&>svg]:hidden">
                        <div className="flex items-center justify-between w-full">
                          <span className="text-lg font-funnel text-violet-600 group-hover:text-violet-700 transition-colors pr-4">
                            {item.question}
                          </span>
                          <Plus className="h-5 w-5 shrink-0 text-violet-600 transition-transform duration-200 group-data-[state=open]:hidden" />
                          <Minus className="h-5 w-5 shrink-0 text-violet-600 transition-transform duration-200 group-data-[state=closed]:hidden" />
                        </div>
                      </AccordionTrigger>
                      <AccordionContent className="text-base pt-4 pb-0">{item.answer}</AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              </div>
            </ScrollAnimatedSection>
          </motion.div>

          <motion.div className="w-full max-w-xs mx-auto text-center mt-12" variants={itemVariants}>
            <ShaderButton
              onClick={() => window.location.href = '/'}
              fullWidth={true}
              className="h-12"
              mode="mesh"
              palette="reveal"
            >
              <div className="text-primary-foreground text-base font-funnel font-medium uppercase">
                Discover Your NFT Totem
              </div>
            </ShaderButton>
          </motion.div>
        </motion.div>
      </div>
    </main>
  )
}

// Optimized CollectionTicker with lazy loading
const CollectionTicker = React.lazy(() => import("./collection-ticker").then(module => ({ default: module.CollectionTicker })))
